import {
  Box,
  BoxProps,
  Button,
  Container,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  convertMinutesToHours,
  formatMsToMinutesSeconds as formatTime,
} from "@picovico/utils";
import { BiCalendar, BiChevronLeft } from "react-icons/bi";

import { BsClock } from "react-icons/bs";
import { FiFilm } from "react-icons/fi";
import { weekdays } from "../../constant/weekdays";
import SeatLayoutLoader from "../SeatLayout/SeatLayoutLoader";
import { SeatLayoutWrapperProps } from "./SeatLayoutContainer";

function BigSeatLayoutContainer({
  seatLayoutRenderer,
  screenDetail,
  movieShowDetail,
  showCloseButton = true,
  onClose,
  totalAmount,
  seatLoading = true,
  actionButton = "Proceed",
  actionButtonProps = {},
  showLayoutHeaderProps = {},
  time = 0,
  isContainer,
  selectedSeats,
}: SeatLayoutWrapperProps & BoxProps) {
  const Base = isContainer ? Container : Box;
  return (
    <Box bg="#FFFFFF">
      <Base
        py={{ base: 4, md: 8 }}
        {...showLayoutHeaderProps}
        position="relative"
      >
        <Button
          leftIcon={<BiChevronLeft size={20} />}
          variant="ghost"
          mb={6}
          color="gray.600"
          fontWeight="normal"
          onClick={onClose}
        >
          Go Back
        </Button>

        <VStack spacing={4} mb={8}>
          <Text color="gray.500" fontSize="sm">
            Movie
          </Text>
          <Skeleton isLoaded={!seatLoading}>
            <Heading size="2xl" fontWeight="bold">
              {movieShowDetail?.title}
            </Heading>
          </Skeleton>
        </VStack>

        <HStack
          spacing={{ base: 4, md: 8 }}
          justify="center"
          mb={8}
          flexWrap="wrap"
          color="gray.600"
          fontSize="sm"
        >
          <HStack>
            <Icon as={BsClock} boxSize={4} />
            <Skeleton isLoaded={!seatLoading}>
              <Text>
                {movieShowDetail?.duration &&
                  convertMinutesToHours(movieShowDetail?.duration)}
              </Text>
            </Skeleton>
          </HStack>
          <HStack>
            <Icon as={BsClock} boxSize={4} />
            <Skeleton isLoaded={!seatLoading}>
              <Text>{movieShowDetail?.grade}</Text>
            </Skeleton>
          </HStack>
          <HStack>
            <Icon as={FiFilm} boxSize={4} />
            <Skeleton isLoaded={!seatLoading}>
              <Text>{movieShowDetail?.genre}</Text>
            </Skeleton>
          </HStack>
          <HStack>
            <Icon as={BiCalendar} boxSize={4} />
            <Skeleton isLoaded={!seatLoading}>
              <Text>Release Date: {movieShowDetail?.release_date}</Text>
            </Skeleton>
          </HStack>
        </HStack>

        <Divider />

        <Flex
          direction={{
            base: "column-reverse",
            md: "column",
          }}
        >
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              lg: "repeat(6, 1fr)",
            }}
            gap={0}
            bg="white"
            borderRadius="lg"
            overflow="hidden"
            boxShadow="sm"
            borderColor="gray.200"
            mt={{
              base: 4,
              md: 0,
            }}
          >
            <GridItem
              borderRight="1px"
              borderBottom={{ base: "1px", lg: "none" }}
              borderColor="gray.200"
              p={{ base: 3, md: 6 }}
            >
              <VStack align="start" spacing={3}>
                <HStack color="blue.500" fontSize="sm" fontWeight="medium">
                  <Text>Date</Text>
                </HStack>
                <VStack align="start" spacing={1}>
                  <Skeleton isLoaded={!seatLoading}>
                    <Text
                      fontSize={{ base: "lg", md: "2xl" }}
                      fontWeight="bold"
                    >
                      {movieShowDetail?.date}
                    </Text>
                  </Skeleton>
                  <Skeleton isLoaded={!seatLoading}>
                    <Text fontSize="sm" color="gray.600">
                      {weekdays[new Date(movieShowDetail?.date || "").getDay()]}
                    </Text>
                  </Skeleton>
                </VStack>
              </VStack>
            </GridItem>

            <GridItem
              borderRight={{ base: "none", lg: "1px" }}
              borderBottom={{ base: "1px", lg: "none" }}
              borderColor="gray.200"
              p={{ base: 3, md: 6 }}
            >
              <VStack align="start" spacing={3}>
                <HStack color="blue.500" fontSize="sm" fontWeight="medium">
                  <Text>Time</Text>
                </HStack>

                <Skeleton isLoaded={!seatLoading}>
                  <Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="bold">
                    {movieShowDetail?.time}
                  </Text>
                </Skeleton>
              </VStack>
            </GridItem>

            <GridItem
              borderRight="1px"
              borderBottom={{ base: "1px", lg: "none" }}
              borderColor="gray.200"
              p={{ base: 3, md: 6 }}
            >
              <VStack align="start" spacing={3}>
                <HStack color="blue.500" fontSize="sm" fontWeight="medium">
                  <Text>Hall</Text>
                </HStack>
                <Skeleton isLoaded={!seatLoading}>
                  <Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="bold">
                    {screenDetail?.screenName}
                  </Text>
                </Skeleton>
              </VStack>
            </GridItem>

            <GridItem
              borderRight={{ base: "none", lg: "1px" }}
              borderBottom={{ base: "1px", lg: "none" }}
              borderColor="gray.200"
              p={{ base: 3, md: 6 }}
            >
              <VStack align="start" spacing={3}>
                <Text color="blue.500" fontSize="sm" fontWeight="medium">
                  Location
                </Text>
                <VStack align="start" spacing={1}>
                  <Skeleton isLoaded={!seatLoading}>
                    <Text
                      fontSize={{ base: "lg", md: "2xl" }}
                      fontWeight="bold"
                    >
                      {screenDetail?.cinemaHall}
                    </Text>
                  </Skeleton>
                  <Skeleton isLoaded={!seatLoading}>
                    <Text fontSize="sm" color="gray.600">
                      {screenDetail?.location}
                    </Text>
                  </Skeleton>
                </VStack>
              </VStack>
            </GridItem>

            <GridItem
              borderRight="1px"
              borderBottom={{ base: "none", lg: "none" }}
              borderColor="gray.200"
              p={{ base: 3, md: 6 }}
            >
              <VStack align="start" spacing={3}>
                <HStack color="blue.500" fontSize="sm" fontWeight="medium">
                  <Text>No. of Tickets</Text>
                  {/* <Icon as={FiEdit2} boxSize={3} /> */}
                </HStack>
                <VStack align="start" spacing={1}>
                  <Skeleton isLoaded={!seatLoading}>
                    <Text
                      fontSize={{ base: "lg", md: "2xl" }}
                      fontWeight="bold"
                    >
                      {selectedSeats?.length}
                    </Text>
                  </Skeleton>
                </VStack>
              </VStack>
            </GridItem>

            <GridItem p={{ base: 3, md: 6 }}>
              <VStack align="start" spacing={3}>
                <Text color="blue.500" fontSize="sm" fontWeight="medium">
                  Total Price
                </Text>
                <Skeleton isLoaded={!seatLoading}>
                  <Text fontSize={{ base: "lg", md: "2xl" }} fontWeight="bold">
                    Rs. {totalAmount}
                  </Text>
                </Skeleton>
              </VStack>
            </GridItem>
          </Grid>
          <Flex direction="column">
            {" "}
            <Flex
              justify="space-between"
              align="center"
              color="gray.500"
              fontSize={{ base: "md", md: "xl" }}
              my={{ base: 6, md: 12 }}
            >
              <Text display={{ base: "none", md: "block" }}>
                {selectedSeats?.length} Seats Choosen
              </Text>
              <Text
                aria-live="polite"
                aria-label={`Elapsed time: ${formatTime(time)}`}
              >
                Time Remaining to book: {formatTime(time)}
              </Text>
            </Flex>
            <Box
              borderRadius={{ base: "12px", md: "20px" }}
              border="1px solid #ACE1FF"
              boxShadow={{ base: "0 2px 4px -1px #ACE1FF", md: "0 4px 6px -1px #ACE1FF, 0 2px 4px -1px #ACE1FF" }}
              px={{ base: 1, md: 6 }}
              py={{ base: 2, md: 4 }}
              overflowX="auto"
            >
              {seatLoading ? (
                <Box p="20px">
                  <SeatLayoutLoader />
                </Box>
              ) : (
                seatLayoutRenderer
              )}
            </Box>
          </Flex>
        </Flex>
      </Base>
      <VStack mt="2rem" bg="#F0FAFF" p={{ base: 4, md: 8 }}>
        <Button w={{ base: "100%", md: "354px" }} {...actionButtonProps}>
          {actionButton}
        </Button>
        {showCloseButton && (
          <Button
            bg="transparent"
            color="black"
            w={{ base: "100%", md: "354px" }}
            _hover={{}}
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
      </VStack>
    </Box>
  );
}

export default BigSeatLayoutContainer;
